import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefreshCcw, X } from 'lucide-react';
import { startVersionCheck, stopVersionCheck, clearCachesAndReload } from '@/utils/versionCheck';

export const UpdateNotification: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    // Start version checking
    startVersionCheck(() => {
      setShowNotification(true);
    });

    // Listen for chunk load failures (from lazyWithRetry)
    const handleChunkLoadFailed = () => {
      setShowNotification(true);
    };
    window.addEventListener('app-chunk-load-failed', handleChunkLoadFailed);

    // Clean up on unmount
    return () => {
      stopVersionCheck();
      window.removeEventListener('app-chunk-load-failed', handleChunkLoadFailed);
    };
  }, []);

  const handleUpdate = async () => {
    setIsReloading(true);
    await clearCachesAndReload();
  };

  const handleDismiss = () => {
    setShowNotification(false);
    // Re-check in 30 minutes
    setTimeout(() => {
      startVersionCheck(() => {
        setShowNotification(true);
      });
    }, 30 * 60 * 1000);
  };

  if (!showNotification) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
      <Card className="p-4 shadow-lg border-primary/20 bg-background/95 backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-1">Update Available</h4>
            <p className="text-sm text-muted-foreground">
              A new version of the app is available. Refresh to get the latest features and improvements.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleUpdate}
                disabled={isReloading}
                className="gap-2"
              >
                <RefreshCcw className={`h-3 w-3 ${isReloading ? 'animate-spin' : ''}`} />
                {isReloading ? 'Refreshing...' : 'Refresh Now'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDismiss}
                disabled={isReloading}
              >
                Later
              </Button>
            </div>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 -mt-1 -mr-1"
            onClick={handleDismiss}
            disabled={isReloading}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};